import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import {
  TenantBrand,
  TENANT_BRANDS,
  resolveSubdomainTenant,
} from '../config/tenant-branding';

export interface TenantBrandingContextValue {
  brand: TenantBrand;
}

const TenantBrandingContext = createContext<TenantBrandingContextValue>({
  brand: TENANT_BRANDS.default,
});

// Extract ?tenant= param from both window.location.search and hash-embedded query
const extractTenantParam = (): string | undefined => {
  const { hash } = window.location;
  const queryStart = hash.indexOf('?');

  if (queryStart !== -1) {
    // slice (not split) so additional '?' chars in a malformed URL stay in the query string
    const queryPortion = hash.slice(queryStart + 1);
    const tenantParam = new URLSearchParams(queryPortion).get('tenant');
    if (tenantParam) {
      return tenantParam.trim();
    }
  }

  // The app uses hash history, so the param normally lives after the '#'. We also honor it
  // on the real query string so it works whether a user types ?tenant= before or after the hash.
  const fallbackTenantParam = new URLSearchParams(window.location.search).get('tenant');
  return fallbackTenantParam?.trim() ?? undefined;
};

// Remove ?tenant= param from both real query string and hash-router query via history.replaceState.
// Called at module level in index.tsx AFTER initializeTenantBranding() captures the param.
export const stripTenantParamFromUrl = (): void => {
  try {
    const {
      origin, pathname, search, hash,
    } = window.location;

    const searchParams = new URLSearchParams(search);
    const searchHadTenant = searchParams.has('tenant');
    if (searchHadTenant) {
      searchParams.delete('tenant');
    }

    let newHash = hash;
    const hashQueryStart = hash.indexOf('?');
    if (hashQueryStart !== -1) {
      const hashPath = hash.slice(0, hashQueryStart);
      const hashParams = new URLSearchParams(hash.slice(hashQueryStart + 1));
      if (hashParams.has('tenant')) {
        hashParams.delete('tenant');
        const remainingHashQuery = hashParams.toString();
        newHash = remainingHashQuery ? `${hashPath}?${remainingHashQuery}` : hashPath;
      }
    }

    if (!searchHadTenant && newHash === hash) {
      return;
    }

    const newSearch = searchParams.toString();
    const newUrl = `${origin}${pathname}${newSearch ? `?${newSearch}` : ''}${newHash}`;
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    // Best-effort cleanup; never block startup on a URL-rewrite failure.
  }
};

// Resolve tenant brand with priority: query param > env var > subdomain.
// Pure — does NOT strip the URL; that is the caller's responsibility.
const resolveTenantBrand = (): TenantBrand => {
  // Priority 1: query param
  const queryParam = extractTenantParam();
  if (queryParam && TENANT_BRANDS[queryParam]) {
    return TENANT_BRANDS[queryParam];
  }

  // Priority 2: env var
  const envOverride = process.env.REACT_APP_TENANT_OVERRIDE;
  if (envOverride && TENANT_BRANDS[envOverride]) {
    return TENANT_BRANDS[envOverride];
  }

  // Priority 3: subdomain resolution
  return resolveSubdomainTenant();
};

// Module-level cache populated by initializeTenantBranding(). Must be called in index.tsx
// BEFORE stripTenantParamFromUrl() — same read-then-strip pattern as initializeApiBaseUrl.
let initializedBrand: TenantBrand | null = null;

export const initializeTenantBranding = (): void => {
  initializedBrand = resolveTenantBrand();
};

// Get the initialized brand for use at module level (e.g., in components outside provider tree)
export const getInitializedBrand = (): TenantBrand => {
  return initializedBrand ?? TENANT_BRANDS.default;
};

// Side-effect-only component that sets document title and favicon
export const TenantDocumentShell = () => {
  const { brand } = useTenantBranding();

  useEffect(() => {
    document.title = brand.brandName;
  }, [ brand.brandName ]);

  useEffect(() => {
    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.querySelector<HTMLLinkElement>('link[rel="shortcut icon"]');
    }

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }

    favicon.href = brand.faviconUrl;
  }, [ brand.faviconUrl ]);

  return null;
};

export const TenantBrandingProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useMemo((): TenantBrandingContextValue => ({
    brand: initializedBrand ?? TENANT_BRANDS.default,
  }), []);

  return (
    <TenantBrandingContext.Provider value={value}>
      <TenantDocumentShell />
      {children}
    </TenantBrandingContext.Provider>
  );
};

export const useTenantBranding = () => useContext(TenantBrandingContext);
